// /events/:eventId/tournament/board — the projector view. Phase 3.
//
// A shared screen for the room: the whole draw, at a size that reads from the
// back, updating itself. §8 of docs/plan-tournaments.md argues this is where the
// value of the feature actually is, and the reason is the roster: a hundred
// players at a mahjong tournament are not a hundred app accounts, so the
// walk-ins have no phone to be told anything on. Without a shared screen they
// are told by somebody reading names out.
//
// Three decisions worth stating, because each has an obvious wrong version:
//
//   • ⚠ IT IS AUTHENTICATED. No public link, no share token. A board URL that
//     worked without signing in would be a brand new unauthenticated surface
//     that lists the full name of every member at a private event, and it would
//     be pasted into a group chat once and live forever. The organizer's laptop
//     is signed in; that is enough. It reads through the same endpoint as the
//     player view and needs MEMBER, like every other tournament read.
//   • IT NEVER WRITES. No draw button, no seating editor, nothing to fat-finger
//     on a screen pointed at a room. `canManage` is not consulted at all here.
//   • IT AUTO-REFRESHES UNCONDITIONALLY, unlike the player view, which suppresses
//     its own echo per tab. This is the one surface that must act on the
//     organizer's OWN change — they draw the round from their phone and turn to
//     the screen expecting it to have moved. That is the entire reason frames
//     are suppressed per TAB rather than per user; see `api/clientId.ts`.
//
// Bare chrome (see BARE_PATHS in chrome.ts) so a projector shows the draw and
// not a nav bar, and it carries its own way back like /goodbye does.

import { ApiClientError, fetchTournament } from '../api/client.js';
import { navigate, onLeave, type View } from '../router.js';
import { subscribe } from '../realtime.js';
import { el } from '../dom.js';
import { tournamentChannelName, type TournamentState } from '@msc/shared';

/**
 * A score with its sign and separators. Same rule as the tournament page's.
 *
 * The plus matters more here than anywhere: at projector size a column where
 * only the negatives are marked makes the reader work out that the unmarked
 * numbers are gains, from thirty feet away.
 */
function formatScore(score: number): string {
  const body = Math.abs(score).toLocaleString();
  if (score > 0) return `+${body}`;
  if (score < 0) return `-${body}`;
  return '0';
}

/**
 * The backstop poll. Slower than the player view's, on purpose.
 *
 * A projector is a big screen nobody is anxiously staring at between rounds, and
 * realtime is the fast path. This is only here for the frame that never
 * arrives -- most plausibly because a second web box has no `WS_PEERS` set, in
 * which case the frame reaches half the room and nothing anywhere logs it.
 */
const POLL_MS = 90000;

export const tournamentBoardView: View = async (outlet, ctx) => {
  const eventId = Number(ctx.params.eventId);
  const main = el('main', { class: 'board' }, [el('p', { class: 'board-wait' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(eventId) || eventId <= 0) {
    main.replaceChildren(el('p', { class: 'board-wait' }, 'Invalid event.'));
    return;
  }

  let state: TournamentState | null = null;

  const poll = window.setInterval(() => void load(), POLL_MS);
  // No echo suppression and no `canManage` branch: see the note at the top.
  const unsubscribe = subscribe(tournamentChannelName(eventId), (frame) => {
    if (frame.event !== 'tournament.changed') return;
    void load();
  });
  onLeave(() => {
    window.clearInterval(poll);
    unsubscribe();
  });

  async function load(): Promise<void> {
    try {
      state = await fetchTournament(eventId);
      render();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/login', true);
        return;
      }
      // ⚠ A failed refresh must NOT blank the screen. The room is looking at
      // this, and a wall of "could not load" where the draw used to be reads as
      // the tournament having been cancelled. Once something has rendered, a
      // later failure leaves the last good draw up and the next poll recovers.
      if (state === null) {
        main.replaceChildren(
          el(
            'p',
            { class: 'board-wait' },
            err instanceof ApiClientError && err.status === 404
              ? 'No tournament on this event yet.'
              : 'Could not load the tournament.',
          ),
        );
      }
    }
  }

  function render(): void {
    const s = state;
    if (!s) return;

    const round = s.rounds.find((r) => r.roundNo === s.currentRound);
    const tables = round?.tables ?? [];
    const drawn = tables.some((t) => t.seats.length > 0);

    main.replaceChildren(
      el('div', { class: 'board-head' }, [
        el('h1', {}, s.eventTitle || 'Tournament'),
        el(
          'p',
          { class: 'board-round' },
          s.status === 'complete'
            ? 'Finished'
            : s.currentRound === 0
              ? 'Not started'
              : `Round ${s.currentRound}`,
        ),
      ]),
      ...(drawn
        ? [
            el('div', { class: 'board-grid' }, tables.map(tableCard)),
            ...(round && round.byes.length ? [byeLine(round.byes.map((b) => b.displayName))] : []),
            ...(s.standings.some((r) => r.roundsPlayed > 0) ? [standingsStrip(s)] : []),
          ]
        : [
            el(
              'p',
              { class: 'board-wait' },
              s.status === 'complete'
                ? 'This tournament is over.'
                : s.currentRound === 0
                  ? 'Waiting for the first round.'
                  : `Round ${s.currentRound} is being drawn.`,
            ),
          ]),
      // Its own way back, because the chrome is hidden on this path.
      el('p', { class: 'board-exit' }, [
        el('a', { href: `/events/${eventId}/tournament` }, 'Leave the board'),
      ]),
    );
  }

  function tableCard(table: TournamentState['rounds'][number]['tables'][number]): HTMLElement {
    return el('section', { class: 'board-table' }, [
      el('h2', {}, `Table ${table.tableNo}`),
      el(
        'ul',
        {},
        table.seats.map((seat) =>
          el('li', {}, [
            // The wind first and quiet, the NAME loud: somebody scanning this
            // screen from thirty feet away is looking for their own name, and
            // reads the wind only once they have found it.
            el('span', { class: 'board-wind' }, seat.seatLabel),
            el('span', { class: 'board-name' }, seat.displayName),
            // Only once entered. An empty column of zeroes on the wall would
            // read as everybody having scored nothing rather than as a round
            // still being played.
            ...(seat.score === null
              ? []
              : [el('span', { class: 'board-score' }, formatScore(seat.score))]),
          ]),
        ),
      ),
    ]);
  }

  /**
   * The top of the standings, along the bottom of the screen.
   *
   * ⚠ TOP EIGHT, not everybody. This is the surface §8 argued for -- a screen a
   * room glances at between rounds -- and a hundred-row leaderboard at
   * projector size is several screens of scrolling that nobody will do. The
   * full table is on the tournament page, which is where somebody looking for
   * their own 47th place goes.
   *
   * Shown at all only once something has been scored, so an unscored first
   * round does not put a wall of zeroes on the wall.
   */
  function standingsStrip(s: TournamentState): HTMLElement {
    const top = s.standings.filter((r) => !r.withdrawn).slice(0, 8);
    return el('div', { class: 'board-standings' }, [
      el('h2', {}, s.status === 'complete' ? 'Final standings' : 'Standings'),
      el(
        'ol',
        {},
        top.map((row) =>
          el('li', { class: row.playerId === s.winnerPlayerId ? 'board-lead' : '' }, [
            el('span', { class: 'board-wind' }, String(row.rank)),
            el('span', { class: 'board-name' }, row.displayName),
            el('span', { class: 'board-score' }, formatScore(row.total)),
          ]),
        ),
      ),
    ]);
  }

  function byeLine(names: readonly string[]): HTMLElement {
    return el('p', { class: 'board-byes' }, [
      el('span', { class: 'board-wind' }, 'Sitting out'),
      el('span', { class: 'board-name' }, names.join(' · ')),
    ]);
  }

  await load();
};
